import React from "react";
import { Alert } from "reactstrap";
import {
    Container,
    Row,
    Col,
    Card,
    CardBody,
    Input,
    Button,
    ListGroup,
    ListGroupItem
} from "reactstrap";
import { useNavigate, useParams, Navigate } from "react-router-dom";
import { sessionEnded } from "../user/SessionEnded";
import Api from "../../components/helpers/Api";
import SeoNoIndex from "../../components/helpers/SeoNoIndex";

function toSortKey(name) {
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return name.toLowerCase();
    const surname = parts[parts.length - 1];
    const firstNames = parts.slice(0, -1).join(' ');
    return `${surname} ${firstNames}`.toLowerCase();
}

function toDisplayName(name) {
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return name;
    const surname = parts[parts.length - 1];
    const firstNames = parts.slice(0, -1).join(' ');
    return `${surname}, ${firstNames}`;
}

class AdminAuthorDetail extends React.Component {
    state = {
        isLoading: true,
        authorName: "",
        associatedSongs: [],
        searchQuery: "",
        searchResults: [],
        saveSuccess: false,
        saveError: null,
        saving: false,
        unauthorized: false,
        // Merge state
        allAuthors: null,
        mergeSearchQuery: "",
        mergeTarget: null,
        mergeConfirming: false,
        merging: false,
        mergeError: null,
        // Delete state
        deleteConfirming: false,
        deleting: false,
        deleteError: null
    };

    componentDidMount() {
        try {
            const customer = sessionStorage.getItem("customer");
            const parsed = customer ? JSON.parse(customer) : null;
            if (!parsed?.user?.isAdmin) {
                this.setState({ unauthorized: true });
                return;
            }
            const { authorId } = this.props.params;
            this.loadAuthor(authorId);
        } catch (e) {
            console.error("AdminAuthorDetail componentDidMount error:", e);
            this.setState({ unauthorized: true });
        }
    }

    componentDidUpdate(prevProps) {
        const { authorId } = this.props.params;
        if (authorId !== prevProps.params.authorId) {
            this.setState({
                isLoading: true,
                authorName: "",
                associatedSongs: [],
                searchQuery: "",
                searchResults: [],
                saveSuccess: false,
                saveError: null,
                saving: false,
                allAuthors: null,
                mergeSearchQuery: "",
                mergeTarget: null,
                mergeConfirming: false,
                merging: false,
                mergeError: null,
                deleteConfirming: false,
                deleting: false,
                deleteError: null
            });
            this.loadAuthor(authorId);
        }
    }

    loadAuthor(id) {
        Api.get(`/authors/${id}`)
            .then(res => {
                const songs = Array.isArray(res.data.songs) ? res.data.songs : [];
                this.setState({
                    authorName: res.data.name,
                    associatedSongs: [...songs].sort((a, b) =>
                        a.title.localeCompare(b.title)
                    ),
                    isLoading: false
                });
            })
            .catch(err => {
                console.error("Error loading author:", err);
                this.setState({ isLoading: false });
            });
    }

    handleSaveName = () => {
        const { authorId } = this.props.params;

        this.setState({ saving: true, saveError: null, saveSuccess: false });

        Api.put(`/authors/${authorId}`, {
            id: authorId,
            name: this.state.authorName
        })
            .then(res => {
                this.setState({ authorName: res.data.name, saveSuccess: true });
                setTimeout(() => this.setState({ saveSuccess: false }), 3000);
            })
            .catch(err => {
                if (err.response?.data?.error) {
                    this.setState({ saveError: err.response.data.error });
                } else {
                    console.error("Failed to update author", err);
                    this.setState({ saveError: "Failed to save. Please try again." });
                }
            })
            .finally(() => this.setState({ saving: false }));
    };

    handleSearch = (e) => {
        e.preventDefault();
        const q = this.state.searchQuery.trim();
        if (!q) return;

        Api.get(`/words/search?q=${encodeURIComponent(q)}`)
            .then(res => this.setState({ searchResults: res.data }))
            .catch(console.error);
    };

    handleAdd = (song) => {
        const { authorId } = this.props.params;
        Api.post(`/words/${song.id}/authors/${authorId}`)
            .then(() =>
                this.setState(state => ({
                    associatedSongs: [...state.associatedSongs, song].sort((a, b) =>
                        a.title.localeCompare(b.title)
                    )
                }))
            )
            .catch(console.error);
    };

    handleRemove = (songId) => {
        const { authorId } = this.props.params;
        Api.delete(`/words/${songId}/authors/${authorId}`)
            .then(() =>
                this.setState(state => ({
                    associatedSongs: state.associatedSongs.filter(s => s.id !== songId)
                }))
            )
            .catch(console.error);
    };

    // ── Merge handlers ────────────────────────────────────────────────────────

    handleLoadMergeAuthors = () => {
        if (this.state.allAuthors !== null) return; // already loaded
        Api.get("/authors")
            .then(({ data }) => {
                const sorted = (data || []).sort((a, b) =>
                    toSortKey(a.name).localeCompare(toSortKey(b.name))
                );
                this.setState({ allAuthors: sorted });
            })
            .catch(console.error);
    };

    handleMergeSearchChange = (e) => {
        this.setState({ mergeSearchQuery: e.target.value, mergeTarget: null, mergeConfirming: false });
    };

    handleSelectMergeTarget = (author) => {
        this.setState({ mergeTarget: author, mergeConfirming: true, mergeSearchQuery: "" });
    };

    handleCancelMerge = () => {
        this.setState({ mergeTarget: null, mergeConfirming: false, mergeError: null });
    };

    handleDeleteAuthor = () => {
        const { authorId } = this.props.params;
        this.setState({ deleting: true, deleteError: null });
        Api.delete(`/authors/${authorId}`)
            .then(() => this.props.navigate("/admin/authors"))
            .catch(err => {
                console.error("Delete failed", err);
                this.setState({
                    deleteError: "Delete failed. Please try again.",
                    deleting: false,
                    deleteConfirming: false
                });
            });
    };

    handleConfirmMerge = () => {
        const { authorId } = this.props.params;
        const { mergeTarget } = this.state;

        this.setState({ merging: true, mergeError: null });

        Api.post(`/authors/${authorId}/merge-into/${mergeTarget.id}`)
            .then(() => {
                this.props.navigate(`/admin/author/${mergeTarget.id}`);
            })
            .catch(err => {
                console.error("Merge failed", err);
                this.setState({
                    mergeError: "Merge failed. Please try again.",
                    merging: false
                });
            });
    };

    render() {
        if (!sessionStorage.getItem("customer")) return sessionEnded();
        if (this.state.unauthorized) return <Navigate to="/" />;

        const {
            isLoading,
            authorName,
            associatedSongs,
            searchQuery,
            searchResults,
            saveSuccess,
            saveError,
            saving,
            allAuthors,
            mergeSearchQuery,
            mergeTarget,
            mergeConfirming,
            merging,
            mergeError,
            deleteConfirming,
            deleting,
            deleteError
        } = this.state;

        const { authorId } = this.props.params;

        const mergeSearchResults = allAuthors && mergeSearchQuery.trim().length >= 2
            ? allAuthors.filter(a =>
                a.id !== authorId &&
                a.name.toLowerCase().includes(mergeSearchQuery.toLowerCase())
              ).slice(0, 10)
            : [];

        return (
            <>
                <SeoNoIndex noindex={true} />

                <section className="section section-lg section-shaped pb-10">
                    <div className="shape shape-style-1 shape-default">
                        <span /><span /><span /><span /><span /><span /><span /><span /><span />
                    </div>
                    <Container className="py-lg-md d-flex">
                        <Row className="align-items-center">
                            <Col lg="12">
                                <h1 className="display-3 text-white">
                                    Edit Author:&nbsp;<span>{authorName}</span>
                                </h1>
                            </Col>
                        </Row>
                    </Container>
                </section>

                <Container fluid className="pb-5">
                    <Row className="justify-content-center">
                        <Col xl="10" lg="12">
                            <Card className="shadow border-0">
                                <CardBody>

                                    <Button
                                        color="link"
                                        onClick={() => this.props.navigate("/admin/authors")}
                                        className="mb-3 p-0"
                                    >
                                        &larr; Back to list
                                    </Button>

                                    <Container className="mt-4">

                                        {saveSuccess && (
                                            <Alert color="success">Author updated successfully</Alert>
                                        )}

                                        {saveError && (
                                            <Alert color="danger">{saveError}</Alert>
                                        )}

                                        <h5 className="mb-2">Author Name</h5>

                                        <Row className="mb-4">
                                            <Col>
                                                <Input
                                                    type="text"
                                                    value={authorName}
                                                    onChange={(e) =>
                                                        this.setState({ authorName: e.target.value })
                                                    }
                                                />
                                            </Col>
                                            <Col xs="auto">
                                                <Button
                                                    color="primary"
                                                    onClick={this.handleSaveName}
                                                    disabled={saving}
                                                >
                                                    {saving ? "Saving..." : "Save"}
                                                </Button>
                                                &nbsp;
                                                <Button
                                                    color="secondary"
                                                    onClick={() => this.props.navigate("/admin/authors")}
                                                >
                                                    Back
                                                </Button>
                                            </Col>
                                        </Row>

                                        {isLoading ? (
                                            <div>Loading…</div>
                                        ) : (
                                            <>
                                                <h5>
                                                    Associated Songs
                                                    <span className="text-muted small ml-2">
                                                        ({associatedSongs.length})
                                                    </span>
                                                </h5>

                                                <ListGroup className="mb-4">
                                                    {associatedSongs.length > 0 ? (
                                                        associatedSongs.map(s => (
                                                            <ListGroupItem
                                                                key={s.id}
                                                                className="d-flex justify-content-between align-items-center"
                                                            >
                                                                <span
                                                                    style={{ cursor: "pointer" }}
                                                                    onClick={() =>
                                                                        this.props.navigate(`/admin/song/${s.id}`)
                                                                    }
                                                                >
                                                                    <strong>{s.title}</strong>
                                                                    {s.alternativeTitle && (
                                                                        <> <em>({s.alternativeTitle})</em></>
                                                                    )}
                                                                </span>
                                                                <Button
                                                                    size="sm"
                                                                    color="danger"
                                                                    onClick={() => this.handleRemove(s.id)}
                                                                >
                                                                    Remove
                                                                </Button>
                                                            </ListGroupItem>
                                                        ))
                                                    ) : (
                                                        <ListGroupItem>No songs linked.</ListGroupItem>
                                                    )}
                                                </ListGroup>

                                                <h5>Add Songs to Author</h5>

                                                <form onSubmit={this.handleSearch} className="mb-3 d-flex">
                                                    <Input
                                                        type="text"
                                                        placeholder="Search songs by title…"
                                                        value={searchQuery}
                                                        onChange={(e) =>
                                                            this.setState({ searchQuery: e.target.value })
                                                        }
                                                    />
                                                    <Button color="primary" type="submit" className="ml-2">
                                                        Search
                                                    </Button>
                                                </form>

                                                <ListGroup className="mb-5">
                                                    {searchResults.map(s => {
                                                        const already = associatedSongs.some(a => a.id === s.id);
                                                        return (
                                                            <ListGroupItem
                                                                key={s.id}
                                                                className="d-flex justify-content-between align-items-center"
                                                            >
                                                                <span
                                                                    style={{ cursor: "pointer" }}
                                                                    onClick={() =>
                                                                        this.props.navigate(`/admin/song/${s.id}`)
                                                                    }
                                                                >
                                                                    {s.title}
                                                                    {s.alternativeTitle && (
                                                                        <> <em>({s.alternativeTitle})</em></>
                                                                    )}
                                                                </span>
                                                                {already ? (
                                                                    <span className="badge badge-secondary">
                                                                        Already linked
                                                                    </span>
                                                                ) : (
                                                                    <Button
                                                                        size="sm"
                                                                        color="success"
                                                                        onClick={() => this.handleAdd(s)}
                                                                    >
                                                                        Add
                                                                    </Button>
                                                                )}
                                                            </ListGroupItem>
                                                        );
                                                    })}
                                                    {searchResults.length === 0 && searchQuery && (
                                                        <ListGroupItem>No matching songs.</ListGroupItem>
                                                    )}
                                                </ListGroup>

                                                {/* ── Merge section ─────────────────────────────── */}
                                                <hr />

                                                <h5 className="mt-4 text-danger">Merge Author</h5>

                                                <p className="text-muted small">
                                                    Move all songs from <strong>{authorName}</strong> into
                                                    another author. This action cannot be undone.
                                                </p>

                                                {mergeError && (
                                                    <Alert color="danger">{mergeError}</Alert>
                                                )}

                                                {!mergeConfirming ? (
                                                    <>
                                                        <Input
                                                            type="text"
                                                            placeholder="Search for target author (min. 2 characters)…"
                                                            value={mergeSearchQuery}
                                                            onChange={this.handleMergeSearchChange}
                                                            onFocus={this.handleLoadMergeAuthors}
                                                            className="mb-2"
                                                        />

                                                        {allAuthors === null && mergeSearchQuery.length > 0 && (
                                                            <p className="text-muted small">Loading authors…</p>
                                                        )}

                                                        {mergeSearchResults.length > 0 && (
                                                            <ListGroup>
                                                                {mergeSearchResults.map(a => (
                                                                    <ListGroupItem
                                                                        key={a.id}
                                                                        className="d-flex justify-content-between align-items-center"
                                                                        style={{ cursor: "pointer" }}
                                                                        onClick={() => this.handleSelectMergeTarget(a)}
                                                                    >
                                                                        {toDisplayName(a.name)}
                                                                        <span className="text-muted small ml-2">
                                                                            {a.id}
                                                                        </span>
                                                                    </ListGroupItem>
                                                                ))}
                                                            </ListGroup>
                                                        )}

                                                        {allAuthors !== null &&
                                                            mergeSearchQuery.trim().length >= 2 &&
                                                            mergeSearchResults.length === 0 && (
                                                            <p className="text-muted small">No matching authors.</p>
                                                        )}
                                                    </>
                                                ) : (
                                                    <Alert color="warning">
                                                        <p className="mb-3">
                                                            <strong>Are you sure?</strong> All{' '}
                                                            <strong>{associatedSongs.length} song{associatedSongs.length !== 1 ? 's' : ''}</strong>{' '}
                                                            from <strong>{authorName}</strong> will be moved to{' '}
                                                            <strong>{mergeTarget.name}</strong>.
                                                        </p>
                                                        <Button
                                                            color="danger"
                                                            onClick={this.handleConfirmMerge}
                                                            disabled={merging}
                                                            className="mr-2"
                                                        >
                                                            {merging ? "Merging…" : `Confirm — merge into ${mergeTarget.name}`}
                                                        </Button>
                                                        <Button
                                                            color="secondary"
                                                            onClick={this.handleCancelMerge}
                                                            disabled={merging}
                                                        >
                                                            Cancel
                                                        </Button>
                                                    </Alert>
                                                )}

                                                {/* ── Delete section ────────────────────────────── */}
                                                <hr />

                                                <h5 className="mt-4 text-danger">Delete Author</h5>

                                                <p className="text-muted small">
                                                    Permanently delete <strong>{authorName}</strong>. This cannot be undone.
                                                </p>

                                                {deleteError && (
                                                    <Alert color="danger">{deleteError}</Alert>
                                                )}

                                                {!deleteConfirming ? (
                                                    <Button
                                                        color="danger"
                                                        outline
                                                        onClick={() => this.setState({ deleteConfirming: true })}
                                                    >
                                                        Delete Author
                                                    </Button>
                                                ) : (
                                                    <Alert color="danger">
                                                        <p className="mb-3">
                                                            <strong>Are you sure?</strong> <strong>{authorName}</strong> will be permanently deleted.
                                                        </p>
                                                        <Button
                                                            color="danger"
                                                            onClick={this.handleDeleteAuthor}
                                                            disabled={deleting}
                                                            className="mr-2"
                                                        >
                                                            {deleting ? "Deleting…" : "Confirm — delete author"}
                                                        </Button>
                                                        <Button
                                                            color="secondary"
                                                            onClick={() => this.setState({ deleteConfirming: false, deleteError: null })}
                                                            disabled={deleting}
                                                        >
                                                            Cancel
                                                        </Button>
                                                    </Alert>
                                                )}
                                            </>
                                        )}
                                    </Container>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </>
        );
    }
}

export default withRouter(AdminAuthorDetail);

function withRouter(Component) {
    return props => {
        const navigate = useNavigate();
        const params = useParams();
        return <Component {...props} navigate={navigate} params={params} />;
    };
}
